import { MoreHorizontal } from 'lucide-react'
import {
  type ComponentType,
  createContext,
  type ReactNode,
  useContext,
  useId,
  useMemo,
  useRef,
} from 'react'
import { Sheet } from './Sheet'
import { useFits } from './useFits'
import { usePopover } from './usePopover'
import './overflow-group.css'

const CloseGroup = createContext<() => void>(() => {})

/**
 * Shut the collapsed group from inside it.
 *
 * The group holds CONTROLS rather than menu items, so selecting one is not
 * dismissal: a toggle stays a toggle in here and a select needs its own click to
 * open. Which children end the group is therefore the caller's to say, and an
 * action that opens a covering surface is the case that has to — the surface
 * lands over a popover that is still open behind it, and closing that surface
 * reveals it again with nothing having dismissed it.
 *
 * Outside the group it does nothing, so a child that may be composed either way
 * calls it unconditionally.
 */
export function useCloseOverflowGroup(): () => void {
  return useContext(CloseGroup)
}

type IconType = ComponentType<{ size?: number; 'aria-hidden'?: boolean }>

/**
 * A group of chrome that becomes ONE entry point where there isn't room for it.
 *
 * ## Why this exists
 *
 * A band of tools has three ways to answer a narrow screen and two of them are
 * wrong. It can shrink its tools — and a button whose label is clipped names
 * nothing. It can wrap to a second row — and then it is a second story of
 * chrome above every page, which grows a third when somebody adds a tool. Or it
 * can collapse, whole, into a trigger that opens the same set in a popover.
 *
 * Only the third is a strategy, and the reason is item count. A wrapped band
 * fits whatever it happens to hold today: two page actions fit, three do not,
 * and the layout has to be re-tuned for the third. A collapsed band looks
 * identical with two tools or with seven, because what is on the screen is one
 * trigger either way. **Nothing here reads how many children it was given, and
 * that is the design rather than an omission.**
 *
 * The goal on a phone was never to show everything. It is that everything stays
 * reachable, through an entry point that is always visible.
 *
 * ## How it decides
 *
 * By measuring whether the contents fit (`useFits`), not by a width somebody
 * picked. A threshold fails from both directions: 40rem overflowed a six-tool
 * toolbar on a 1280px desktop, and a bigger number collapsed a two-tool one on a
 * tablet with room for both. The question was never how wide the container is.
 *
 * Both renderings are in the markup and one is hidden (see `overflow-group.css`).
 * `display: none` takes the hidden one out of the accessibility tree too, so a
 * screen reader is offered one copy, not two.
 *
 * **The constraint that follows, and it is the price of this approach:** children
 * must be controlled and hold no state of their own, because both copies mount.
 * Buttons, a `Toggle` driven by props, a `Select` — all fine. A child holding
 * a draft in local state would have two drafts.
 */
export function OverflowGroup({
  /** Names the collapsed trigger — for a screen reader, and its tooltip. */
  label,
  short,
  icon: Icon = MoreHorizontal,
  /** Which edge the popover hangs from; `end` suits a trailing group. */
  align = 'end',
  surface = 'popover',
  forceCollapsed = false,
  expendable,
  fold,
  children,
}: {
  label: string
  /**
   * A word for the trigger where there is room to print one — the phone's single
   * row of chrome, where each entry point has a third of the width. In the app's
   * own toolbars the trigger stays a glyph beside laid-out tools; in the row it
   * sits next to other triggers, and a row of identical dots is a set of things
   * you have to open to tell apart. Always paired with a meaningful `icon`.
   */
  short?: string
  icon?: IconType
  align?: 'start' | 'end'
  /**
   * Where the collapsed set opens.
   *
   * `popover` (the default) is right for a group of buttons: they are already the
   * size they want to be, and a small surface beside the trigger keeps them next
   * to where you reached.
   *
   * `panel` is for a group of *controls* — selects, a view switch, a count. A
   * 13rem popover made every one of them the width of a phrase, and a select
   * inside it opened its own menu into a surface with no room for the options: a
   * filter you cannot read the choices in. There is no reason to squeeze here, so
   * the set opens on a panel that gives each control the full width.
   */
  surface?: 'popover' | 'panel'
  /**
   * Collapse whether or not the contents fit.
   *
   * For the phone's row of chrome, where every cell is an entry point of one
   * shape. A lone select that happened to fit in its third rendered as a select
   * between two buttons, so the row's look depended on which tools a screen had —
   * the count-dependence this component exists to remove, arriving by the back door.
   * Everywhere else the measurement decides, which is the whole point of it.
   */
  forceCollapsed?: boolean
  /**
   * The one item in the group that is a **fact rather than a control** — a count.
   *
   * It is dropped when the group is short of room, before anything collapses, and
   * it is restored when the room comes back. the priority order puts facts about a
   * thing behind the thing itself: a strip one item too wide should give up its
   * count, not hide a filter. Anything you can act on belongs in `children`.
   */
  expendable?: ReactNode
  /**
   * Controls that fold behind the entry point BEFORE the rest of the group
   * does — a list's filters, ahead of its total and its view switch. Where they
   * do not fit, they go behind the trigger and the rest stays laid out; only
   * where the rest does not fit either does the whole group collapse. Measured
   * the way `expendable` is, and it takes that slot: a group has one or the
   * other.
   */
  fold?: ReactNode
  children: ReactNode
}) {
  /* A panel brings its own dismissal, and it is not a descendant of this group —
     so outside-click has to be off, or the first tap inside it closes it. */
  const { open, setOpen, ref, panelRef } = usePopover({
    dismissOnOutside: surface === 'popover',
  })
  const popId = useId()
  const inlineRef = useRef<HTMLDivElement>(null)
  const expendableRef = useRef<HTMLDivElement>(null)
  const foldTriggerRef = useRef<HTMLButtonElement>(null)
  const measured = useFits(
    ref,
    inlineRef,
    fold || expendable ? expendableRef : undefined,
    fold ? foldTriggerRef : undefined,
  )
  const mode = forceCollapsed ? 'collapsed' : measured
  const close = useMemo(() => () => setOpen(false), [setOpen])

  return (
    <CloseGroup.Provider value={close}>
      <div
        className="overflowgroup"
        ref={ref}
        data-fit={mode}
        data-folds={fold ? '' : undefined}
      >
        {/* Laid out, where there is room for it. */}
        <div className="overflowgroup__inline" ref={inlineRef}>
          {fold ? (
            <>
              {/* Where the folded controls were, the way back to them — beside
                  them in the row, so it is found where they are missed. */}
              <button
                ref={foldTriggerRef}
                type="button"
                className="overflowgroup__trigger overflowgroup__foldtrigger"
                data-open={open ? '' : undefined}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-label={label}
                onClick={() => setOpen((o) => !o)}
              >
                <Icon size={18} aria-hidden />
                {short ? (
                  <span className="overflowgroup__word">{short}</span>
                ) : null}
              </button>
              <div className="overflowgroup__fold" ref={expendableRef}>
                {fold}
              </div>
            </>
          ) : null}
          {children}
          {/*
          The one thing here that is not a control, and the first to go: it is a
          fact about the list, so it gives way before a button does rather than
          taking a control down with it. It reappears the moment there is room, and
          it is inside the collapsed set below either way.
        */}
          {expendable && !fold ? (
            <div className="overflowgroup__expendable" ref={expendableRef}>
              {expendable}
            </div>
          ) : null}
        </div>

        {/* The entry point, where there isn't. */}
        <div className="overflowgroup__collapsed">
          <button
            type="button"
            className="overflowgroup__trigger"
            data-open={open ? '' : undefined}
            aria-haspopup="menu"
            aria-expanded={open}
            aria-controls={open ? popId : undefined}
            aria-label={label}
            onClick={() => setOpen((o) => !o)}
          >
            <Icon size={18} aria-hidden />
            {/* In the markup, shown by CSS only where the trigger has room for a
              word — the same approach as the two renderings above. */}
            {short ? (
              <span className="overflowgroup__word">{short}</span>
            ) : null}
          </button>
          {open && surface === 'panel' ? (
            /*
             * A panel, for a group of controls. It is the Sheet's own surface — one
             * panel look and one dismissal in the app — sized to the set rather
             * than to the trigger it opened from, because a select needs room to show
             * what it is offering and a count needs room to be read.
             */
            <Sheet
              title={label}
              ariaLabel={label}
              onClose={() => setOpen(false)}
            >
              <div className="overflowgroup__panel">
                {fold}
                {mode === 'trimmed' && fold ? null : children}
                {expendable}
              </div>
            </Sheet>
          ) : null}
          {open && surface === 'popover' ? (
            /*
             * The same surface a Menu opens — one popover look and one dismissal
             * for the whole app. It holds the group's own controls rather
             * than menu items, so the tools stack at full width: a toggle is
             * still a toggle in here, which is what lets a heterogeneous band
             * collapse without being described as data first.
             */
            <div
              ref={panelRef}
              id={popId}
              className={`overflowgroup__pop${align === 'start' ? ' overflowgroup__pop--start' : ''}`}
              role="menu"
              aria-label={label}
            >
              {fold}
              {mode === 'trimmed' && fold ? null : children}
              {expendable}
            </div>
          ) : null}
        </div>
      </div>
    </CloseGroup.Provider>
  )
}
